import { Component, CUSTOM_ELEMENTS_SCHEMA, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { EditorComponent, HUGERTE_SCRIPT_SRC } from '@hugerte/hugerte-angular';
import { ThemeService } from 'app/services/theme.service';
import { UserService } from 'app/services/user.service';
import { WebsiteService } from 'app/services/website.service';
import { Editor } from 'hugerte';
import { Subject } from 'rxjs';

export type ContentFormat = 'raw' | 'text' | 'html' | 'tree';

@Component({
    selector: 'app-rich-text-editor',
    templateUrl: './rich-text-editor.component.html',
    styleUrls: ['./rich-text-editor.component.scss'],
    standalone: true,
    imports: [EditorComponent],
    providers: [
        { provide: HUGERTE_SCRIPT_SRC, useValue: 'hugerte/hugerte.min.js' }
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],

})
export class RichTextEditorComponent implements OnInit
{
    @Input() id: string;
	@Input() value: string;
	@Input() label: string;
    @Input() editable: boolean = true;
    @Input() format: ContentFormat = "html";
	@Output() mcChange: EventEmitter<string> = new EventEmitter<string>();
    @Output() mcImageUpload: EventEmitter<RichTextEditorComponent> = new EventEmitter<RichTextEditorComponent>();

    richEditor: EditorComponent['init'];
    private imageUploadSubject: Subject<string> = new Subject<string>();

    constructor(
        public websiteService: WebsiteService,
        public userService: UserService,
        private themeService: ThemeService
    ) { }

    ngOnInit() 
    {
        const isDark = this.themeService.isDark();
        const tableGuideStyle = 'table.mce-item-table,table.mce-item-table caption,table.mce-item-table td,table.mce-item-table th{border:0!important;}';

        this.richEditor = {
            base_url: '/hugerte',
            suffix: '.min',
            promotion: false,
            disabled: !this.editable,
            editable_root: this.editable,
            statusbar: false,
            sandbox_iframes: true,

            // Theme
            skin: isDark ? 'oxide-dark' : 'oxide',
            content_css: isDark ? 'dark' : 'default',
            content_style: isDark
                ? 'html{background:#121212;}body{background:#121212;color:rgba(255,255,255,.9);}a{color:#4099ff;}hr{border-color:rgba(255,255,255,.18);}table,th,td{border-color:rgba(255,255,255,.18);}' + tableGuideStyle
                : tableGuideStyle,

            // Following settings based on blog editor from dealer websites
            height: 500,
            plugins: ["image", "table", "help", "lists", "link", "code", "importcss", "anchor"],
            relative_urls: false,
            menubar: false,
            toolbar: this.editable ? 'undo redo | formatselect | bold italic underline strikethrough forecolor backcolor | link anchor image | alignleft aligncenter alignright alignjustify | table | bullist numlist outdent indent | removeformat | cut copy paste pastetext' : false,
            images_upload_handler: this.customImageUploadHandler.bind(this),

        };

        this.setContents();
    }

    setContents()
    {
        this.richEditor = {
            ...this.richEditor,
            init_instance_callback: (editor) => {
                editor.setContent(this.value);
            },
            setup: (editor) => {
                editor.on('Change', () => {
                    this.getEditorContent(editor);
                });
            },            
        };
    }

    getEditorContent(editor: Editor)
    {
        const newContent = editor.getContent({ format: this.format });
        this.mcChange.emit(newContent);
        
    }

    public blob: Blob;
    customImageUploadHandler(blobInfo: any): Promise<string> {
        return new Promise((resolve, reject) => {
            this.blob = blobInfo.blob();
            this.mcImageUpload.emit(this);

            const subscription = this.imageUploadSubject.subscribe({
                next: (uploadedUrl: string) => {
                    if (uploadedUrl) {
                        resolve(uploadedUrl);
                        subscription.unsubscribe();
                    } else {
                        reject('Failed to upload image');
                        subscription.unsubscribe();
                    }
                },
                error: (err) => {
                    var errorMessage = this.userService.handleError(err);
                    reject(errorMessage);
                    subscription.unsubscribe();
                }
            });
        });
    }

    setImageUploadUrl(url: string) {
        this.imageUploadSubject.next(url);
    }

    setImageUploadUrlError(error: any) {
        this.imageUploadSubject.error(error);
    }

}
